/**
 * @file Nav Dropdown.
 */

import { gsap } from 'gsap';

/**
 * Nav Dropdown component.
 */
const vNavDropdown = () => {
	const navDropdowns = document.querySelectorAll('.v-nav-dropdown');

	if (!navDropdowns.length) {
		return;
	}

	const header = document.querySelector('.v-header');
	const submenuClasses = [];

	class SubMenu {
		constructor(submenu, i) {
			this.wrap = submenu;
			this.inner = submenu.querySelector('.v-nav-dropdown__inner');
			this.id = i;
			this.trigger = document.querySelector(`#${this.wrap.getAttribute('aria-labelledby')}`);
			this.close = submenu.querySelector('.v-nav-dropdown__close');
			this.isOpen = false;
			this.isAnimating = false;
			this.animationCompleteCb = null;
			this.inHeader = this.wrap.closest('.v-header') !== null;

			this.init();
		}

		static anyMenuAnimating() {
			return submenuClasses.some((submenuClass) => submenuClass.isAnimating);
		}

		/**
		 * Open submenu.
		 */
		openList() {
			if (this.isOpen || this.constructor.anyMenuAnimating()) {
				return;
			}

			const openLists = submenuClasses.filter((submenuClass) => (
				submenuClass.inHeader === this.inHeader
				&& submenuClass.isOpen
				&& submenuClass.id !== this.id
			));

			if (openLists.length) {
				this.swapList(openLists[0]);
				return;
			}

			this.isAnimating = true;
			gsap.set(this.wrap, {
				display:         'block',
				opacity:         0,
				transformOrigin: 'center top',
			});
			gsap.set([this.inner, this.close], { opacity: 0 });
			gsap.set(this.wrap, { scaleY: 0 });

			gsap.timeline({
				defaults: {
					ease:     'sine.out',
					duration: 0.3,
				},
				onComplete: () => {
					this.isOpen = true;
					this.isAnimating = false;
					gsap.set([header, this.wrap, this.inner, this.close], { clearProps: 'all' });
					this.wrap.classList.add('v-nav-dropdown--open');
					header.classList.add('v-header--sub-list-open');

					if (this.animationCompleteCb) {
						this.animationCompleteCb();
						this.animationCompleteCb = null;
					}
				},
			})
				.fromTo(
					header,
					{ background: 'rgba(255, 255, 255, 0)' },
					{
						background: 'rgba(255, 255, 255, 1)',
						duration:   0.1,
					},
				)
				.to(this.wrap, {
					opacity:  1,
					duration: 0.1,
				}, '<')
				.to(this.wrap, { scaleY: 1 }, '<')
				.to([this.inner, this.close], { opacity: 1 }, '>');
		}

		/**
		 * Close submenu.
		 *
		 * @param {number} playbackSpeed - Speed to play animation at.
		 */
		closeList(playbackSpeed = 1) {
			if (!this.isOpen || this.constructor.anyMenuAnimating()) {
				return;
			}

			gsap.set(this.wrap, { transformOrigin: 'center top' });

			this.isAnimating = true;
			const closeAnimation = gsap.timeline({
				paused:   true,
				defaults: {
					ease:     'sine.out',
					duration: 0.3,
				},
				onComplete: () => {
					this.isOpen = false;
					this.isAnimating = false;
					gsap.set([header, this.wrap, this.inner, this.close], { clearProps: 'all' });
					this.wrap.classList.remove('v-nav-dropdown--open');
					header.classList.remove('v-header--sub-list-open');

					if (this.animationCompleteCb) {
						this.animationCompleteCb();
						this.animationCompleteCb = null;
					}
				},
			})
				.to([this.inner, this.close], { opacity: 0 })
				.to(this.wrap, { scaleY: 0 }, '>')
				.to(this.wrap, {
					opacity:  0,
					duration: 0.2,
				}, '>-0.2')
				.fromTo(
					header,
					{ background: 'rgba(255, 255, 255, 1)' },
					{
						background: 'rgba(255, 255, 255, 0)',
						duration:   0.2,
					},
					'<',
				);

			closeAnimation.timeScale(playbackSpeed).play();
		}

		/**
		 * Move from one open list to another.
		 *
		 * @param {HTMLElement} previousList - Previous open list.
		 */
		swapList(previousList) {
			this.isAnimating = true;
			const currentHeight = previousList.wrap.clientHeight;
			gsap.set(this.wrap, { display: 'block', opacity: 0, transformOrigin: 'center top' });
			gsap.set(this.inner, { opacity: 0 });
			const targetHeight = this.wrap.clientHeight;
			gsap.timeline({
				defaults: {
					ease:     'sine.out',
					duration: 0.3,
				},
				onComplete: () => {
					/* eslint-disable no-param-reassign */
					previousList.isOpen = false;
					previousList.isAnimating = false;
					/* eslint-enable no-param-reassign */
					gsap.set([previousList.wrap, previousList.inner, previousList.close], { clearProps: 'all' });
					previousList.wrap.classList.remove('v-nav-dropdown--open');

					gsap.set(this.wrap, { opacity: 1 });
					gsap.timeline({
						defaults: {
							ease:     'sine.out',
							duration: 0.3,
						},
						onComplete: () => {
							this.isOpen = true;
							this.isAnimating = false;
							gsap.set([this.wrap, this.inner, this.close], { clearProps: 'all' });
							this.wrap.classList.add('v-nav-dropdown--open');

							if (this.animationCompleteCb) {
								this.animationCompleteCb();
								this.animationCompleteCb = null;
							}
						},
					})
						.to(this.inner, {
							opacity: 1,
						});
				},
			})
				.to(previousList.inner, {
					opacity: 0,
				})
				.to(previousList.wrap, {
					height:   targetHeight,
					duration: Math.abs(targetHeight - currentHeight) / 400,
				}, '>');
		}

		init() {
			this.trigger.addEventListener('click', (e) => {
				e.preventDefault();
				if (this.isOpen) {
					this.closeList();
				} else {
					this.openList();
				}
			});

			this.close.addEventListener('click', () => {
				this.closeList();
			});

			document.addEventListener('v-sticky-header-toggle', (e) => {
				if (
					(e.detail.stuck && this.inHeader)
					|| (!e.detail.stuck && !this.inHeader)
				) {
					if (this.isAnimating) {
						this.animationCompleteCb = this.closeList;
					} else {
						this.closeList(!this.inHeader ? 2 : 1);
					}
				}
			});
		}
	}

	[...navDropdowns].forEach((submenu, i) => {
		submenuClasses.push(new SubMenu(submenu, i));
	});
};

export default vNavDropdown;
